import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search, Globe } from 'lucide-react';

// Liste des pays avec codes ISO
const COUNTRIES = [
  { code: 'FR', name: 'France' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'GF', name: 'Guyane française' },
  { code: 'RE', name: 'La Réunion' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'NC', name: 'Nouvelle-Calédonie' },
  { code: 'PF', name: 'Polynésie française' },
  { code: 'BE', name: 'Belgique' },
  { code: 'CH', name: 'Suisse' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MC', name: 'Monaco' },
  { code: 'AD', name: 'Andorre' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'AT', name: 'Autriche' },
  { code: 'ES', name: 'Espagne' },
  { code: 'IT', name: 'Italie' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'IE', name: 'Irlande' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'US', name: 'États-Unis' },
  { code: 'CA', name: 'Canada' },
  { code: 'MA', name: 'Maroc' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'CM', name: 'Cameroun' },
  { code: 'GA', name: 'Gabon' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'RD Congo' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MU', name: 'Maurice' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'ML', name: 'Mali' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'NE', name: 'Niger' },
  { code: 'TD', name: 'Tchad' },
  { code: 'BJ', name: 'Bénin' },
  { code: 'TG', name: 'Togo' },
  { code: 'GN', name: 'Guinée' },
  { code: 'HT', name: 'Haïti' },
];

// URL pour obtenir le drapeau
const getFlagUrl = (countryCode) => {
  return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
};

/**
 * CountrySelect - Sélecteur de pays avec drapeaux
 */
export default function CountrySelect({ 
  value, 
  onChange, 
  placeholder = 'Sélectionner un pays',
  label = 'Pays',
  showLabel = true,
  required = false,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  // Trouver le pays sélectionné
  const selectedCountry = COUNTRIES.find(c => c.code === value || c.name === value);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les pays
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (country) => {
    onChange(country.name);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`space-y-2 ${className}`} ref={dropdownRef}>
      {/* Label */}
      {showLabel && (
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Globe className="h-4 w-4 text-slate-500" />
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Sélecteur */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full flex items-center gap-3 px-4 py-3 
            border-2 rounded-xl transition-all duration-300 bg-white text-left
            ${isOpen 
              ? 'border-blue-500 ring-4 ring-blue-100 shadow-lg' 
              : 'border-slate-200 hover:border-slate-300'
            }
          `}
        >
          {selectedCountry ? (
            <>
              <img 
                src={getFlagUrl(selectedCountry.code)} 
                alt={selectedCountry.name}
                className="w-7 h-5 object-cover rounded shadow-sm"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 30"><rect fill="%23ddd" width="40" height="30"/></svg>';
                }}
              />
              <span className="flex-1 font-medium text-slate-800">{selectedCountry.name}</span>
            </>
          ) : (
            <>
              <Globe className="h-5 w-5 text-slate-400" />
              <span className="flex-1 text-slate-400">{placeholder}</span>
            </>
          )}
          <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 z-50 
                          bg-white rounded-xl border-2 border-slate-200 shadow-2xl
                          max-h-80 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Recherche */}
            <div className="p-3 border-b border-slate-100 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un pays..."
                  className="w-full pl-10 pr-4 py-2 text-sm border-2 border-slate-200 rounded-lg
                             focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  autoFocus
                />
              </div>
            </div>

            {/* Liste des pays */}
            <div className="overflow-y-auto max-h-60 p-2">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleSelect(country)}
                    className={`
                      flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                      transition-all duration-200 text-left
                      ${selectedCountry?.code === country.code
                        ? 'bg-blue-50 border-2 border-blue-200' 
                        : 'hover:bg-slate-50 border-2 border-transparent'}
                    `}
                  >
                    <img 
                      src={getFlagUrl(country.code)} 
                      alt={country.name}
                      className="w-7 h-5 object-cover rounded shadow-sm"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 30"><rect fill="%23ddd" width="40" height="30"/></svg>';
                      }}
                    />
                    <span className="flex-1 text-sm font-medium text-slate-700">{country.name}</span>
                    {selectedCountry?.code === country.code && (
                      <Check className="h-4 w-4 text-blue-500" />
                    )}
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun pays trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { COUNTRIES, getFlagUrl };
